import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { placeholderImage } from '@yourstore/theme';
import type { PreviewCanvasMessage, PreviewDevice, PreviewPage } from '@yourstore/theme';
import { useConsole, useApi, useRead } from '../../context';
import { Confirm, Failure, Loading } from '../../bits';
import { Menu, Segmented, useToast } from '../../kit';
import type { MenuItem } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type { T } from '../../../messages';
import type {
  BrandingDocument,
  BrandingPublished,
  BrandingRead,
  BrandingRolledBack,
  BrandingSection,
  IntakeDraft,
  PageTemplate,
  ZoneReport,
} from '../../../wire';
import {
  HOME,
  addPage,
  duplicateSection,
  forPublish,
  fromStored,
  indexOfKey,
  insertSection,
  moveSection,
  pageSlugs,
  record,
  redo,
  removePage,
  removeSection,
  replace,
  samePublished,
  sectionsOf,
  setPageTitle,
  setSlot,
  startHistory,
  undo,
} from './doc';
import type { Doc, History, PageRef } from './doc';
import type { BlockSpec } from './specs';
import { DATA_SOURCES, blockSpecs, pinnedOf } from './specs';
import { previewOriginFor, previewUrl, useCanvas } from './canvas';
import type { CanvasState } from './canvas';
import { clearDraft, loadDraft, saveDraft } from './draft';
import { Outline } from './Outline';
import { Inspector } from './Inspector';
import { Gallery } from './Gallery';
import { Drawer } from './Drawer';
import { LookControls, PresetSwatches } from './Look';
import { NewPageDialog, PagesPanel, localizedText, undeclaredPageTargets } from './Pages';
import { CssPanel } from './Css';
import { PublishDialog } from './Publish';
import { VersionsDialog } from './Versions';
import { FromAnswersNote, ShoppingNote, TemplatesDialog } from './Starts';
import { canvasLabels, sectionName } from './names';
import './builder.css';

/**
 * **The page builder** (console redesign C2, `docs/console-redesign-plan.md`
 * §3 C; the approved mockup `docs/design/page-builder.html`).
 *
 * Full screen at `#/b/<id>/site/editor`: a slim bar (back, the page, the
 * device, undo and redo, the draft's state, Versions, Publish), the page's
 * outline and its Look on the left, the business's real site in the middle,
 * and the selected section's inspector on the right.
 *
 * ## One document, and who owns it
 *
 * The builder owns the branding document, the undo stack and every write; the
 * canvas owns nothing but the picture (`packages/theme/src/preview.ts`). Every
 * change — a click in the outline, a slot typed in the inspector, a word typed
 * on the canvas — is a new whole document on the stack, a new whole draft to
 * the canvas (debounced while typing), and an autosave to this computer. The
 * document, its validator and the theme contract stay the single source of
 * truth: nothing here is a second model of a page.
 *
 * ## What was the old editor's
 *
 * `TenantBranding.tsx` (the 2,600-line form) and `BrandingPreview.tsx` (its
 * sketch) are gone; every control they had is here — the inspector for a
 * section's slots, the Look drawer for presets, tokens, motion and the site's
 * name, the Pages drawer for content pages and the bar, the stylesheet drawer
 * for custom CSS and the sanitizer's rules, Versions for the rollback, the
 * More menu for templates and discarding.
 */

type Device = PreviewDevice;

/** The page on the canvas: an arranged one, or a catalog page shown as it is. */
type View = PreviewPage;

/** Typing on the canvas or in a field reaches the canvas at most this often. */
const DRAFT_DEBOUNCE_MS = 150;
/** And reaches this computer's storage at most this often. */
const SAVE_DEBOUNCE_MS = 600;

export function PageBuilder({
  tenantId,
  startFrom,
  startTemplate,
  onBack,
  onTemplates,
}: {
  tenantId: string;
  startFrom: IntakeDraft | null;
  startTemplate: PageTemplate | null;
  onBack: () => void;
  onTemplates?: ((templates: readonly PageTemplate[]) => void) | undefined;
}): ReactNode {
  const api = useApi();
  const t = useT();
  const read = useRead(() => api.branding(tenantId), [tenantId]);

  useEffect(() => {
    if (read.value !== null) onTemplates?.(read.value.contract.templates);
    // `onTemplates` is an inline closure at every call site; the read is the trigger.
  }, [read.value]);

  if (read.value === null) {
    return (
      <div className="bld" role="region" aria-label={t('builder.region')}>
        <div className="bld-bar">
          <button type="button" className="bld-back" onClick={onBack}>
            {t('builder.back')}
          </button>
        </div>
        <div className="bld-loading">
          {read.failure !== null ? <Failure failure={read.failure} /> : null}
          {read.loading ? <Loading what={t('loading.theme')} /> : null}
        </div>
      </div>
    );
  }

  return (
    <Workbench
      tenantId={tenantId}
      read={read.value}
      onReload={read.reload}
      startFrom={startFrom}
      startTemplate={startTemplate}
      onBack={onBack}
    />
  );
}

/* ------------------------------------------------------------------ */
/* The workbench                                                        */
/* ------------------------------------------------------------------ */

interface Starting {
  history: History;
  from: 'stored' | 'draft' | 'answers' | 'template';
  savedAt: string | null;
  stale: boolean;
}

function starting(
  tenantId: string,
  read: BrandingRead,
  slotsOf: (type: string) => readonly { name: string; alias?: string | undefined }[],
  startFrom: IntakeDraft | null,
  startTemplate: PageTemplate | null,
): Starting {
  const published = fromStored(read.branding, slotsOf);
  // A hand-off is an explicit choice and wins over a draft on this computer;
  // it starts a history whose first step back is the published page.
  if (startFrom !== null) {
    return {
      history: record(startHistory(published), fromStored(startFrom.document, slotsOf)),
      from: 'answers',
      savedAt: null,
      stale: false,
    };
  }
  if (startTemplate !== null) {
    return {
      history: record(
        startHistory(published),
        fromStored(startTemplate.document as BrandingDocument, slotsOf),
      ),
      from: 'template',
      savedAt: null,
      stale: false,
    };
  }
  const stored = loadDraft(tenantId);
  if (stored !== null) {
    const doc = fromStored(stored.document, slotsOf);
    if (!samePublished(doc, published)) {
      return {
        history: record(startHistory(published), doc),
        from: 'draft',
        savedAt: stored.saved_at,
        stale: stored.base_version !== read.branding_version,
      };
    }
  }
  return { history: startHistory(published), from: 'stored', savedAt: null, stale: false };
}

type Dialog =
  | { kind: 'gallery'; at: number }
  | { kind: 'publish' }
  | { kind: 'versions' }
  | { kind: 'templates' }
  | { kind: 'template'; template: PageTemplate }
  | { kind: 'new-page' }
  | { kind: 'remove'; key: string }
  | { kind: 'discard' };

type DrawerKind = 'look' | 'pages' | 'css';

type SaveState = 'clean' | 'saving' | 'saved' | 'failed';

function Workbench({
  tenantId,
  read,
  onReload,
  startFrom,
  startTemplate,
  onBack,
}: {
  tenantId: string;
  read: BrandingRead;
  onReload: () => void;
  startFrom: IntakeDraft | null;
  startTemplate: PageTemplate | null;
  onBack: () => void;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const toast = useToast();
  const { config } = useConsole();
  const contract = read.contract;
  const specs = useMemo(() => blockSpecs(contract), [contract]);
  const slotsOf = useCallback((type: string) => specs.get(type)?.slots ?? [], [specs]);
  const pinned = pinnedOf(contract);
  const name = read.tenant.name;

  const [start] = useState(() => starting(tenantId, read, slotsOf, startFrom, startTemplate));
  const [history, setHistory] = useState<History>(start.history);
  const [baseline, setBaseline] = useState<Doc>(() => fromStored(read.branding, slotsOf));
  const [baseVersion, setBaseVersion] = useState(read.branding_version);
  const doc = history.present;
  const docRef = useRef(doc);
  docRef.current = doc;

  const [view, setView] = useState<View>({ kind: 'home' });
  const [device, setDevice] = useState<Device>('desktop');
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [drawn, setDrawn] = useState<ReadonlySet<string> | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [drawer, setDrawer] = useState<DrawerKind | null>(null);
  const [zones, setZones] = useState<ZoneReport[]>([]);
  const [shopping, setShopping] = useState<PageTemplate | null>(
    start.from === 'template' ? startTemplate : null,
  );
  const [answers, setAnswers] = useState<IntakeDraft | null>(
    start.from === 'answers' ? startFrom : null,
  );
  const [restoredNote, setRestoredNote] = useState(start.from === 'draft');
  const [published, setPublished] = useState<BrandingPublished | null>(null);
  const [saveState, setSaveState] = useState<SaveState>(
    start.from === 'stored' ? 'clean' : 'saved',
  );

  const page: PageRef | null =
    view.kind === 'home'
      ? HOME
      : view.kind === 'content'
        ? { kind: 'content', slug: view.slug }
        : null;
  const sections = page === null ? [] : sectionsOf(doc, page);
  const unpublished = !samePublished(doc, baseline);
  const slugs = pageSlugs(doc);

  /* ---------------------------------------------------------------- */
  /* Changing the document                                             */
  /* ---------------------------------------------------------------- */

  const change = useCallback((next: Doc, coalesce: string | null = null) => {
    if (next === docRef.current) return;
    docRef.current = next;
    setHistory((h) => record(h, next, coalesce));
  }, []);

  const doUndo = (): void => setHistory((h) => undo(h));
  const doRedo = (): void => setHistory((h) => redo(h));

  /* ---------------------------------------------------------------- */
  /* The canvas                                                        */
  /* ---------------------------------------------------------------- */

  const origin = useMemo(
    () => previewOriginFor(config.sitePreviewOrigin, tenantId, window.location.href),
    [config.sitePreviewOrigin, tenantId],
  );
  const labels = useMemo(() => canvasLabels(t, specs), [t, specs]);
  const rev = useRef(0);
  const draftTimer = useRef<number | null>(null);
  const live = useRef({ view, device, selected, locale, labels });
  live.current = { view, device, selected, locale, labels };

  const onCanvas = useRef<(message: PreviewCanvasMessage) => void>(() => undefined);
  const canvas = useCanvas(origin, (message) => onCanvas.current(message));
  const ready = canvas.state.kind === 'ready';

  /** The whole draft, now. Every other path to the canvas ends here. */
  const sendDraft = useCallback(
    (of: Doc = docRef.current, select?: string | null) => {
      if (draftTimer.current !== null) window.clearTimeout(draftTimer.current);
      draftTimer.current = null;
      const now = { ...live.current, ...(select === undefined ? {} : { selected: select }) };
      rev.current += 1;
      canvas.post({
        type: 'draft',
        rev: rev.current,
        branding: forPublish(of) as Record<string, unknown>,
        page: now.view,
        device: now.device,
        ui_locale: now.locale,
        labels: now.labels,
        selected: now.selected,
      });
    },
    [canvas],
  );

  // Debounced: typing reaches the canvas at most every DRAFT_DEBOUNCE_MS.
  useEffect(() => {
    if (!ready) return undefined;
    if (draftTimer.current !== null) window.clearTimeout(draftTimer.current);
    draftTimer.current = window.setTimeout(() => sendDraft(), DRAFT_DEBOUNCE_MS);
    return undefined;
  }, [doc, view, device, locale, ready, sendDraft]);

  useEffect(
    () => () => {
      if (draftTimer.current !== null) window.clearTimeout(draftTimer.current);
    },
    [],
  );

  const pick = (key: string | null, scroll: boolean): void => {
    setSelected(key);
    canvas.post({ type: 'select', key });
    if (key !== null && scroll) canvas.post({ type: 'scroll-to', key, behavior: 'smooth' });
  };

  onCanvas.current = (message: PreviewCanvasMessage): void => {
    switch (message.type) {
      case 'ready':
        sendDraft();
        return;
      case 'section-rects': {
        if (message.rev !== rev.current) return;
        // A band that drew nothing (a catalog band with nothing to sell) is
        // still framed, at no height: drawn means drawn with some height.
        const keys = new Set(message.sections.filter((s) => s.rect.height > 0).map((s) => s.key));
        setDrawn((prev) =>
          prev !== null && prev.size === keys.size && [...keys].every((k) => prev.has(k))
            ? prev
            : keys,
        );
        return;
      }
      case 'select':
        setSelected(message.key);
        return;
      case 'hover':
        setHovered(message.key);
        return;
      case 'insert-at':
        if (page === null) return;
        setDialog({ kind: 'gallery', at: message.index });
        return;
      case 'section-action':
        act(message.key, message.index, message.action);
        return;
      case 'text-edit': {
        const current = docRef.current;
        const list = page === null ? [] : sectionsOf(current, page);
        const at = indexOfKey(list, message.key);
        const target = list[at];
        const slot =
          target === undefined
            ? undefined
            : specs.get(target.type)?.slots.find((s) => s.name === message.slot);
        if (
          page === null ||
          target === undefined ||
          (slot !== undefined && slot.kind !== 'text' && slot.kind !== 'prose')
        ) {
          canvas.post({
            type: 'edit-text-commit-ack',
            edit_id: message.edit_id,
            accepted: false,
            message: t('builder.canvas.editRefused'),
          });
          return;
        }
        const next = setSlot(current, page, message.key, message.slot, message.text);
        change(next);
        // The draft first, then the answer: the canvas holds the typed words
        // until one of the two arrives, and this order is what keeps the band
        // from flashing back to the old words for a frame.
        sendDraft(next);
        canvas.post({ type: 'edit-text-commit-ack', edit_id: message.edit_id, accepted: true });
        return;
      }
      case 'nav': {
        const target = message.page;
        if (target === undefined) {
          if (message.external) toast(t('builder.canvas.external'));
          return;
        }
        if (target.kind === 'content' && !slugs.includes(target.slug)) {
          toast(t('builder.canvas.noSuchPage'));
          return;
        }
        goTo(target);
        return;
      }
      case 'error':
        if (message.code === 'render-failed') toast(t('builder.canvas.renderFailed'));
        return;
    }
  };

  /* ---------------------------------------------------------------- */
  /* Arranging                                                         */
  /* ---------------------------------------------------------------- */

  const act = (
    key: string,
    index: number,
    action: 'up' | 'down' | 'duplicate' | 'remove',
  ): void => {
    if (page === null) return;
    const list = sectionsOf(docRef.current, page);
    const at = indexOfKey(list, key) >= 0 ? indexOfKey(list, key) : index;
    const target = list[at];
    if (target === undefined) return;
    switch (action) {
      case 'up':
      case 'down': {
        const to = at + (action === 'up' ? -1 : 1);
        if (to < 0 || to >= list.length) return;
        change(moveSection(docRef.current, page, at, to));
        return;
      }
      case 'duplicate': {
        if (specs.get(target.type)?.repeatable !== true) {
          toast(
            t('builder.onlyOne', { name: sectionName(t, target.type, specs.get(target.type)) }),
          );
          return;
        }
        const made = duplicateSection(docRef.current, page, at);
        change(made.doc);
        if (made.id !== null) setSelected(made.id);
        return;
      }
      case 'remove':
        if (target.type === pinned) return;
        setDialog({ kind: 'remove', key });
        return;
    }
  };

  const insert = (type: string, at: number): void => {
    if (page === null) return;
    const made = insertSection(docRef.current, page, at, {
      type,
      slots: starterSlots(t, type, specs.get(type)),
    });
    change(made.doc);
    setSelected(made.id);
    setDialog(null);
    sendDraft(made.doc, made.id);
    canvas.post({ type: 'scroll-to', key: made.id, behavior: 'smooth' });
  };

  /** "+ Add a section" in the rail: at the end, but above a closing footer. */
  const addAtEnd = (): void => {
    const last = sections[sections.length - 1];
    setDialog({
      kind: 'gallery',
      at: last !== undefined && last.type === 'footer' ? sections.length - 1 : sections.length,
    });
  };

  const goTo = (next: View): void => {
    setView(next);
    setSelected(null);
    setDrawn(null);
  };

  /* ---------------------------------------------------------------- */
  /* Autosave, to this computer                                        */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    if (!unpublished) {
      clearDraft(tenantId);
      setSaveState('clean');
      return undefined;
    }
    setSaveState('saving');
    const timer = window.setTimeout(() => {
      const ok = saveDraft(tenantId, {
        base_version: baseVersion,
        document: forPublish(doc),
        saved_at: new Date().toISOString(),
      });
      setSaveState(ok ? 'saved' : 'failed');
    }, SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [doc, unpublished, tenantId, baseVersion]);

  /* ---------------------------------------------------------------- */
  /* Keys                                                              */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey || e.metaKey) || dialog !== null || drawer !== null) return;
      const key = e.key.toLowerCase();
      if (key !== 'z' && key !== 'y') return;
      const target = e.target as HTMLElement | null;
      // A field keeps its own undo: Ctrl+Z in a text box takes back letters.
      if (
        target !== null &&
        (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))
      ) {
        return;
      }
      e.preventDefault();
      if (key === 'y' || e.shiftKey) doRedo();
      else doUndo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, drawer]);

  /* ---------------------------------------------------------------- */
  /* Publishing, restoring, discarding                                 */
  /* ---------------------------------------------------------------- */

  const settle = (branding: BrandingDocument, version: number): void => {
    // The stored bytes replace the typed ones — the teaching step: a content
    // zone now shows what the sanitizer kept, not what was pasted.
    const stored = fromStored(branding, slotsOf);
    setBaseline(stored);
    setBaseVersion(version);
    setHistory((h) => replace(h, stored));
    clearDraft(tenantId);
    setRestoredNote(false);
    onReload();
  };

  const onPublished = (answer: BrandingPublished): void => {
    setZones(answer.zones);
    setPublished(answer);
    setDialog(null);
    settle(answer.branding, answer.branding_version);
    toast(t('builder.published', { version: answer.branding_version }));
  };

  const onRestored = (answer: BrandingRolledBack): void => {
    setZones([]);
    setPublished(null);
    setDialog(null);
    settle(answer.branding, answer.branding_version);
    toast(t('site.restore.done', { from: answer.restored_from, version: answer.branding_version }));
  };

  const dangling = undeclaredPageTargets(doc.nav ?? [], slugs);
  const blocked =
    dangling[0] === undefined
      ? null
      : `${t('branding.navUndeclared', { target: dangling[0] })} ${t('branding.navUndeclaredHint')}`;

  /* ---------------------------------------------------------------- */
  /* What is on screen                                                 */
  /* ---------------------------------------------------------------- */

  const selectedIndex = selected === null ? -1 : indexOfKey(sections, selected);
  const selectedSection = sections[selectedIndex] ?? null;
  const offPage = new Set(
    sections
      .filter((s) => {
        const spec = specs.get(s.type);
        if (spec === undefined || page === null) return false;
        const kind = page.kind;
        const excused = kind === 'home' && contract.home_grandfathered.includes(s.type);
        return !spec.pages.includes(kind) && !excused;
      })
      .map((s) => s.id ?? s.type),
  );
  const hidden = new Set(
    ready && drawn !== null
      ? sections.map((s) => s.id ?? s.type).filter((key) => !drawn.has(key))
      : [],
  );
  const pageLabel =
    page === null
      ? t('builder.page.catalogView')
      : page.kind === 'home'
        ? t('builder.page.home')
        : t('builder.page.named', {
            title: localizedText(doc.pages?.[page.slug]?.title ?? page.slug, locale) || page.slug,
          });

  const moreItems: MenuItem[] = [
    { key: 'pages', label: t('builder.more.pages'), onPick: () => setDrawer('pages') },
    { key: 'css', label: t('builder.more.css'), onPick: () => setDrawer('css') },
    ...(contract.templates.length === 0
      ? []
      : [
          {
            key: 'templates',
            label: t('builder.more.templates'),
            onPick: () => setDialog({ kind: 'templates' }),
          },
        ]),
    {
      key: 'discard',
      label: t('builder.more.discard'),
      tone: 'danger' as const,
      separator: true,
      onPick: () => {
        if (unpublished) setDialog({ kind: 'discard' });
        else toast(t('builder.nothingToDiscard'));
      },
    },
  ];

  return (
    <div className="bld" role="region" aria-label={t('builder.region')}>
      <TopBar
        name={name}
        view={view}
        slugs={slugs}
        doc={doc}
        device={device}
        canUndo={history.past.length > 0}
        canRedo={history.future.length > 0}
        saveState={saveState}
        unpublished={unpublished}
        pagesMax={contract.content_pages.pages_max}
        more={moreItems}
        onBack={onBack}
        onPage={(next) => goTo(next)}
        onNewPage={() => setDialog({ kind: 'new-page' })}
        onDevice={setDevice}
        onUndo={doUndo}
        onRedo={doRedo}
        onVersions={() => setDialog({ kind: 'versions' })}
        onPublish={() => setDialog({ kind: 'publish' })}
      />

      {restoredNote ? (
        <div className="bld-note" role="note">
          <div className="bld-note__head">
            <span>
              {t('builder.restoredDraft', { when: savedWhen(start.savedAt, locale) })}{' '}
              {start.stale ? t('builder.restoredStale') : ''}
            </span>
            <span className="bld-row">
              <button
                type="button"
                className="linkish"
                onClick={() => setDialog({ kind: 'discard' })}
              >
                {t('builder.more.discard')}
              </button>
              <button type="button" className="linkish" onClick={() => setRestoredNote(false)}>
                {t('builder.note.dismiss')}
              </button>
            </span>
          </div>
        </div>
      ) : null}
      {shopping !== null ? (
        <ShoppingNote template={shopping} onDismiss={() => setShopping(null)} />
      ) : null}
      {answers !== null ? (
        <FromAnswersNote draft={answers} onDismiss={() => setAnswers(null)} />
      ) : null}
      {published !== null ? (
        <div className="bld-note bld-note--ok" role="status">
          <div className="bld-note__head">
            <span>
              {t('branding.published', {
                version: published.branding_version,
                bytes: published.document_bytes,
                effective: published.effective,
              })}
            </span>
            <button type="button" className="linkish" onClick={() => setPublished(null)}>
              {t('builder.note.dismiss')}
            </button>
          </div>
          {published.warnings.length === 0 ? null : (
            <ul className="bld-plain">
              {published.warnings.map((warning) => (
                <li key={warning} className="warn">
                  {warning}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <div className="bld-work">
        <aside className="bld-rail" aria-label={t('builder.rail')}>
          <h2 className="bld-label">{pageLabel}</h2>
          {page === null ? (
            <div className="bld-stack">
              <p className="bld-hint">{t('builder.page.catalogNote')}</p>
              <button
                type="button"
                className="button button--sm"
                onClick={() => goTo({ kind: 'home' })}
              >
                {t('builder.page.backHome')}
              </button>
            </div>
          ) : (
            <>
              <Outline
                sections={sections}
                specs={specs}
                pinned={page.kind === 'home' ? pinned : ''}
                selected={selected}
                hovered={hovered}
                hidden={hidden}
                offPage={offPage}
                label={t('builder.outline.label', { page: pageLabel })}
                onSelect={(key) => pick(key, true)}
                onHover={(key) => {
                  setHovered(key);
                  canvas.post({ type: 'hover', key });
                }}
                onMove={(from, to) => change(moveSection(docRef.current, page, from, to))}
              />
              <p id="bld-outline-keys" className="bld-hint">
                {t('builder.outline.keys')}
              </p>
              <button type="button" className="bld-addbtn" onClick={addAtEnd}>
                {t('builder.add')}
              </button>
            </>
          )}

          <div className="bld-look">
            <h2 className="bld-label">{t('builder.look.title')}</h2>
            <PresetSwatches
              contract={contract}
              value={doc.preset ?? contract.presets[0]?.name ?? ''}
              onPick={(preset) => change({ ...docRef.current, preset })}
            />
            <button type="button" className="linkish" onClick={() => setDrawer('look')}>
              {t('builder.look.open')}
            </button>
            <button type="button" className="linkish" onClick={() => setDrawer('pages')}>
              {t('builder.more.pages')}
            </button>
            <button type="button" className="linkish" onClick={() => setDrawer('css')}>
              {t('builder.more.css')}
            </button>
          </div>
        </aside>

        <Stage
          origin={origin}
          state={canvas.state}
          device={device}
          canvasRef={canvas.frame}
          generation={canvas.generation}
          onLoad={canvas.onLoad}
          onRetry={canvas.retry}
        />

        <aside className="bld-inspector" aria-label={t('builder.inspector.label')}>
          {page === null ? (
            <p className="bld-hint">{t('builder.page.catalogNote')}</p>
          ) : (
            <Inspector
              tenantId={tenantId}
              section={selectedSection}
              index={selectedIndex}
              count={sections.length}
              spec={selectedSection === null ? undefined : specs.get(selectedSection.type)}
              pinned={page.kind === 'home' && selectedSection?.type === pinned}
              pageKind={page.kind}
              hidden={selectedSection !== null && hidden.has(selectedSection.id ?? '')}
              zones={zones}
              zonePrefix={page.kind === 'home' ? 'sections' : `pages.${page.slug}.sections`}
              policyRules={read.policy_rules}
              onSlot={(slot, value) => {
                if (selectedSection?.id === undefined) return;
                change(
                  setSlot(docRef.current, page, selectedSection.id, slot, value),
                  `${selectedSection.id}.${slot}`,
                );
              }}
              onMove={(delta) => {
                const to = selectedIndex + delta;
                if (to < 0 || to >= sections.length) return;
                change(moveSection(docRef.current, page, selectedIndex, to));
              }}
              onDuplicate={() => {
                if (selectedSection?.id !== undefined)
                  act(selectedSection.id, selectedIndex, 'duplicate');
              }}
              onRemove={() => {
                if (selectedSection?.id !== undefined)
                  setDialog({ kind: 'remove', key: selectedSection.id });
              }}
            />
          )}
        </aside>
      </div>

      {drawer === 'look' ? (
        <Drawer title={t('builder.look.drawer')} onClose={() => setDrawer(null)}>
          <LookControls
            contract={contract}
            preset={doc.preset ?? contract.presets[0]?.name ?? ''}
            tokens={doc.tokens ?? {}}
            motion={doc.motion ?? contract.motion.default}
            siteTitle={doc.site_title ?? ''}
            tagline={doc.tagline ?? ''}
            fallbackName={name}
            onPreset={(preset) => change({ ...docRef.current, preset })}
            onTokens={(tokens) => change({ ...docRef.current, tokens }, 'tokens')}
            onMotion={(motion) => change({ ...docRef.current, motion })}
            onSiteTitle={(site_title) => change({ ...docRef.current, site_title }, 'site_title')}
            onTagline={(tagline) => change({ ...docRef.current, tagline }, 'tagline')}
          />
        </Drawer>
      ) : null}
      {drawer === 'pages' ? (
        <Drawer title={t('builder.more.pagesTitle')} onClose={() => setDrawer(null)}>
          <PagesPanel
            pages={slugs.map((slug) => ({ slug, title: doc.pages?.[slug]?.title ?? '' }))}
            nav={doc.nav ?? []}
            limits={contract.content_pages}
            onOpen={(slug) => {
              setDrawer(null);
              goTo({ kind: 'content', slug });
            }}
            onTitle={(slug, title) =>
              change(setPageTitle(docRef.current, slug, title), `title.${slug}`)
            }
            onRemove={(slug) => {
              change(removePage(docRef.current, slug));
              if (view.kind === 'content' && view.slug === slug) goTo({ kind: 'home' });
            }}
            onNew={() => setDialog({ kind: 'new-page' })}
            onNav={(nav) => change({ ...docRef.current, nav }, 'nav')}
          />
        </Drawer>
      ) : null}
      {drawer === 'css' ? (
        <Drawer title={t('builder.more.cssTitle')} onClose={() => setDrawer(null)}>
          <CssPanel
            contract={contract}
            value={doc.custom_css ?? ''}
            pinnedTo={read.custom_css_contract_version}
            zones={zones}
            onChange={(custom_css) => change({ ...docRef.current, custom_css }, 'custom_css')}
          />
        </Drawer>
      ) : null}

      {dialog?.kind === 'gallery' && page !== null ? (
        <Gallery
          specs={specs}
          pageKind={page.kind}
          present={sections}
          onPick={(type) => insert(type, dialog.at)}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'remove' ? (
        <RemoveConfirm
          t={t}
          section={sections.find((s) => s.id === dialog.key) ?? null}
          specs={specs}
          onConfirm={() => {
            if (page !== null) {
              const at = indexOfKey(sectionsOf(docRef.current, page), dialog.key);
              change(removeSection(docRef.current, page, at));
            }
            if (selected === dialog.key) setSelected(null);
            setDialog(null);
          }}
          onCancel={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'publish' ? (
        <PublishDialog
          tenantId={tenantId}
          name={name}
          document={forPublish(doc)}
          blocked={blocked}
          onPublished={onPublished}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'versions' ? (
        <VersionsDialog
          tenantId={tenantId}
          read={read}
          unpublished={unpublished}
          onRestored={onRestored}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'templates' ? (
        <TemplatesDialog
          templates={contract.templates}
          onPick={(template) => setDialog({ kind: 'template', template })}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'template' ? (
        <Confirm
          tone="primary"
          title={t('branding.templateTitle', { label: dialog.template.label })}
          consequence={t('builder.templates.consequence')}
          confirmLabel={t('branding.useTemplate')}
          busy={false}
          failure={null}
          onConfirm={() => {
            change(fromStored(dialog.template.document as BrandingDocument, slotsOf));
            setShopping(dialog.template);
            goTo({ kind: 'home' });
            setDialog(null);
          }}
          onCancel={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'new-page' ? (
        <NewPageDialog
          limits={contract.content_pages}
          taken={slugs}
          onAdd={(slug, title) => {
            change(addPage(docRef.current, slug, title));
            setDialog(null);
            setDrawer(null);
            goTo({ kind: 'content', slug });
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'discard' ? (
        <Confirm
          title={t('builder.discard.title')}
          consequence={t('builder.discard.consequence')}
          confirmLabel={t('builder.discard.go')}
          busy={false}
          failure={null}
          onConfirm={() => {
            change(baseline);
            clearDraft(tenantId);
            setRestoredNote(false);
            setDialog(null);
          }}
          onCancel={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The bar                                                              */
/* ------------------------------------------------------------------ */

const NEW_PAGE = '__new__';

function TopBar({
  name,
  view,
  slugs,
  doc,
  device,
  canUndo,
  canRedo,
  saveState,
  unpublished,
  pagesMax,
  more,
  onBack,
  onPage,
  onNewPage,
  onDevice,
  onUndo,
  onRedo,
  onVersions,
  onPublish,
}: {
  name: string;
  view: View;
  slugs: readonly string[];
  doc: Doc;
  device: Device;
  canUndo: boolean;
  canRedo: boolean;
  saveState: SaveState;
  unpublished: boolean;
  pagesMax: number;
  more: MenuItem[];
  onBack: () => void;
  onPage: (view: View) => void;
  onNewPage: () => void;
  onDevice: (device: Device) => void;
  onUndo: () => void;
  onRedo: () => void;
  onVersions: () => void;
  onPublish: () => void;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const value = view.kind === 'home' ? '' : view.kind === 'content' ? view.slug : '__catalog__';
  return (
    <div className="bld-bar">
      <button type="button" className="bld-back" onClick={onBack}>
        {t('builder.backTo', { name })}
      </button>
      <span className="bld-who">
        {t('builder.website')}{' '}
        <small>{unpublished ? t('builder.state.unpublished') : t('builder.state.clean')}</small>
      </span>
      <select
        className="bld-pagepick"
        aria-label={t('builder.page.pick')}
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          if (next === NEW_PAGE) onNewPage();
          else if (next === '') onPage({ kind: 'home' });
          else if (next !== '__catalog__') onPage({ kind: 'content', slug: next });
        }}
      >
        <option value="">{t('builder.page.home')}</option>
        {slugs.map((slug) => (
          <option key={slug} value={slug}>
            {localizedText(doc.pages?.[slug]?.title ?? slug, locale) || slug}
          </option>
        ))}
        {value === '__catalog__' ? (
          <option value="__catalog__">{t('builder.page.catalogView')}</option>
        ) : null}
        {pagesMax === 0 ? null : <option value={NEW_PAGE}>{t('builder.page.new')}</option>}
      </select>
      <span className="bld-spacer" />
      <Segmented
        label={t('builder.device.label')}
        value={device}
        options={[
          { id: 'desktop', label: t('builder.device.desktop') },
          { id: 'tablet', label: t('builder.device.tablet') },
          { id: 'phone', label: t('builder.device.phone') },
        ]}
        onChange={onDevice}
      />
      <button
        type="button"
        className="bld-iconbtn"
        aria-label={t('builder.undo')}
        title={t('builder.undoKeys')}
        disabled={!canUndo}
        onClick={onUndo}
      >
        ↶
      </button>
      <button
        type="button"
        className="bld-iconbtn"
        aria-label={t('builder.redo')}
        title={t('builder.redoKeys')}
        disabled={!canRedo}
        onClick={onRedo}
      >
        ↷
      </button>
      <span className="bld-saved" role="status" aria-live="polite">
        {saveState === 'saving'
          ? t('builder.save.saving')
          : saveState === 'saved'
            ? t('builder.save.saved')
            : saveState === 'failed'
              ? t('builder.save.failed')
              : t('builder.save.clean')}
      </span>
      <button type="button" className="button button--sm" onClick={onVersions}>
        {t('builder.versions.open')}
      </button>
      <Menu label="⋯" ariaLabel={t('builder.more.label')} items={more} />
      <button type="button" className="button button--sm button--primary" onClick={onPublish}>
        {t('builder.publish.open')}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The stage                                                            */
/* ------------------------------------------------------------------ */

const WIDTHS: Record<Device, string> = { desktop: '1100px', tablet: '768px', phone: '390px' };

function Stage({
  origin,
  state,
  device,
  canvasRef,
  generation,
  onLoad,
  onRetry,
}: {
  origin: string | null;
  state: CanvasState;
  device: Device;
  canvasRef: RefObject<HTMLIFrameElement | null>;
  generation: number;
  onLoad: () => void;
  onRetry: () => void;
}): ReactNode {
  const t = useT();
  const unavailable = state.kind === 'off' || state.kind === 'failed';
  return (
    <div className="bld-stage">
      {unavailable ? (
        <div className="bld-stage__off" role="status">
          <strong>{t('builder.canvas.offTitle')}</strong>
          <p>
            {state.kind === 'off'
              ? t('builder.canvas.noAddress')
              : t('builder.canvas.failed', { reason: state.reason })}
          </p>
          <p className="bld-hint">{t('builder.canvas.keepGoing')}</p>
          {state.kind === 'failed' ? (
            <button type="button" className="button button--sm" onClick={onRetry}>
              {t('builder.canvas.retry')}
            </button>
          ) : null}
        </div>
      ) : null}
      {origin === null || state.kind === 'failed' ? null : (
        <div className={`bld-device bld-device--${device}`} style={{ maxWidth: WIDTHS[device] }}>
          {state.kind === 'connecting' ? (
            <p className="bld-stage__connecting" role="status">
              {t('builder.canvas.connecting')}
            </p>
          ) : null}
          <iframe
            key={generation}
            ref={canvasRef}
            className="bld-canvas"
            title={t('builder.canvas.title')}
            src={previewUrl(origin)}
            onLoad={onLoad}
          />
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small pieces                                                         */
/* ------------------------------------------------------------------ */

function RemoveConfirm({
  t,
  section,
  specs,
  onConfirm,
  onCancel,
}: {
  t: T;
  section: BrandingSection | null;
  specs: Map<string, BlockSpec>;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactNode {
  const name = section === null ? '' : sectionName(t, section.type, specs.get(section.type));
  return (
    <Confirm
      title={t('builder.remove.title', { name })}
      consequence={t('builder.remove.consequence', { name })}
      confirmLabel={t('builder.remove.go')}
      busy={false}
      failure={null}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}

function savedWhen(iso: string | null, locale: string): string {
  if (iso === null) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(at);
}

/**
 * **What a new section starts with**, so it is visible the moment it lands:
 * a band with nothing in it draws nothing, and "I added a gallery and nothing
 * happened" is the first thing an operator would otherwise learn. Words in the
 * console's language (they are placeholders the operator replaces), pictures
 * from the theme's own placeholder, and nothing at all for a band that fills
 * itself from the business's data.
 */
export function starterSlots(
  t: T,
  type: string,
  spec: BlockSpec | undefined,
): Record<string, unknown> {
  if (spec === undefined || DATA_SOURCES[type] !== undefined) return {};
  const has = (name: string): boolean => spec.slots.some((slot) => slot.name === name);
  const slots: Record<string, unknown> = {};
  const name = sectionName(t, type, spec);
  if (has('headline') && type !== 'hero') slots['headline'] = name;
  const picture = (label: string): string => placeholderImage('3:2', label);
  switch (type) {
    case 'faq':
      slots['items'] = [{ q: t('builder.starter.question'), a: t('builder.starter.answer') }];
      break;
    case 'testimonials':
      slots['items'] = [{ title: t('builder.starter.who'), text: t('builder.starter.quote') }];
      break;
    case 'features':
      slots['items'] = [1, 2, 3].map((n) => ({
        icon: 'star',
        title: t('builder.starter.feature', { n }),
        text: t('builder.starter.featureText'),
      }));
      break;
    case 'team':
    case 'showcase':
      slots['items'] = [1, 2].map((n) => ({
        image: picture(String(n)),
        title: t('builder.starter.panel', { n }),
        text: t('builder.starter.panelText'),
      }));
      break;
    case 'gallery':
      slots['images'] = [1, 2, 3].map((n) => ({ src: picture(String(n)), alt: '', caption: '' }));
      break;
    case 'pricing':
      slots['items'] = [
        { name: t('builder.starter.plan', { n: 1 }), price: '$' },
        { name: t('builder.starter.plan', { n: 2 }), price: '$' },
      ];
      break;
    case 'story':
      slots['body'] = t('builder.starter.body');
      break;
    case 'notice':
      slots['text'] = t('builder.starter.notice');
      break;
    default:
      break;
  }
  return slots;
}
